<script setup lang="ts">
  // Shared read-only order view tree (layout ported from cartadigital-pos
  // PosOrderViewWorkspaceBase.vue). Used by PosV2ViewWorkspace (interactive
  // tabs) and, later, by the preview modals (mode="preview").
  // The money pane re-hosts ModalPosViewOrderPaymentStatus AS-IS: it injects
  // 'order' and self-contains payments/discounts/tips/invoicing.
  import { computed, provide, ref, watch } from 'vue';
  import { onClickOutside } from '@vueuse/core';
  import { PermissionType } from '@/enums/PermissionType';
  import { IOrder } from '@/interfaces/IOrder';
  import { isViewCompact } from '@/domain/pos/pos-compaction';
  import { useAuth } from '@/composition/use-auth';
  import {
    ORDER_CLUB_CONTEXT_KEY,
    useOrderClubContext,
  } from '@/composition/use-order-club-context';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import ModalPosViewOrderPaymentStatus from '@/components/modals/pos/view-order/ModalPosViewOrderPaymentStatus.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosV2ViewInfo from './PosV2ViewInfo.vue';
  import PosV2ViewItems from './PosV2ViewItems.vue';
  import PosV2ViewMoneyStrip from './PosV2ViewMoneyStrip.vue';
  import PosV2ViewStatusStepper from './PosV2ViewStatusStepper.vue';

  const props = withDefaults(
    defineProps<{
      // Optional so Vue 2 skips runtime type-checks when null is passed.
      order?: IOrder | null;
      loading?: boolean;
      mode?: 'interactive' | 'preview';
    }>(),
    {
      order: null,
      loading: false,
      mode: 'interactive',
    }
  );

  const hasOrder = computed(() => !!props.order);

  // Children (including the re-hosted payment component) inject Ref<IOrder>;
  // they only render when hasOrder is true, so the non-null cast is safe.
  const providedOrder = computed(() => props.order as IOrder);
  provide('order', providedOrder);
  provide('posV2ViewMode', props.mode);
  // Re-hosted legacy components (payment status) switch to their flat skin.
  provide('posV2Host', true);
  // Club member figures + identification grant shared by the member panel,
  // the info pills and the compact strip (one fetch per order change). The
  // endpoint needs OrdersManage: history-only viewers (Ventas) keep the
  // embed figures without a 401 per order open.
  const auth = useAuth();
  const canFetchClubContext = computed(() =>
    auth.hasPermission(PermissionType.OrdersManage)
  );
  provide(
    ORDER_CLUB_CONTEXT_KEY,
    useOrderClubContext(providedOrder, canFetchClubContext)
  );

  //#region Compact mode (short desktop viewports)

  // Stable (IME-blind) size: the keyboard opening must not flip the layout
  // and close the detail pane via the watch below.
  const { width: winWidth, height: winHeight } = useStableWindowSize();
  const compact = computed(() =>
    isViewCompact(winWidth.value, winHeight.value)
  );

  const detailOpen = ref(false);
  watch(compact, () => (detailOpen.value = false));

  const stripRef = ref<InstanceType<typeof PosV2ViewMoneyStrip>>();
  const moneyPaneRef = ref<HTMLElement>();

  // Close the overlay on outside clicks, but never when the click lands on
  // the strip (the Detalle button already toggles), inside a body-level
  // vue-js-modal dialog opened FROM the pane (discount edit/delete, etc.),
  // or inside a Vuetify menu (invoice print/download split menu attaches to
  // the [data-app] root, outside the pane).
  onClickOutside(
    moneyPaneRef,
    () => {
      if (compact.value && detailOpen.value) detailOpen.value = false;
    },
    { ignore: [stripRef, '.vm--container', '.v-menu__content'] }
  );

  //#endregion
</script>

<template>
  <div class="pos-v2-view-workspace-base">
    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <span class="loading-spinner"></span>
      <p class="state-text">{{ $t('posV2.loadingOrder') }}</p>
    </div>

    <!-- Error state -->
    <div v-else-if="!hasOrder" class="error-state">
      <PosUiIcon name="alert-circle-outline" class="error-icon" />
      <p class="state-text">{{ $t('posV2.orderLoadError') }}</p>
    </div>

    <!-- Split layout -->
    <template v-else>
      <div class="view-main">
        <div class="view-left">
          <PosV2ViewInfo />
          <PosV2ViewStatusStepper />
        </div>

        <div class="view-right" :class="{ 'is-compact': compact }">
          <PosV2ViewItems class="view-items" />
          <PosV2ViewMoneyStrip
            v-if="compact"
            ref="stripRef"
            class="money-strip"
            @toggle-detail="detailOpen = !detailOpen"
          />
          <!-- Always mounted (inject chain + reactivity intact); compact
               mode turns it into a v-show overlay above the strip. -->
          <div
            ref="moneyPaneRef"
            v-show="!compact || detailOpen"
            class="money-pane"
          >
            <ModalPosViewOrderPaymentStatus />
          </div>
        </div>
      </div>

      <slot name="footer" />
    </template>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-view-workspace-base {
    @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-bg-neutral-100;

    .loading-state,
    .error-state {
      @apply tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-text-neutral-600;
    }

    .loading-spinner {
      @apply tw-h-9 tw-w-9 tw-animate-spin tw-rounded-full tw-border-4 tw-border-orange-600 tw-border-t-transparent;
    }

    .error-icon {
      @apply tw-text-4xl tw-text-red-500;
    }

    .state-text {
      @apply tw-mb-0 tw-text-base;
    }

    .view-main {
      @apply tw-flex tw-flex-1 tw-flex-col tw-gap-0 tw-overflow-y-auto tw-p-0;
      @apply lg:tw-flex-row lg:tw-gap-3 lg:tw-overflow-hidden lg:tw-p-3;
    }

    .view-left {
      @apply tw-flex tw-w-full tw-flex-shrink-0 tw-flex-col tw-gap-2 tw-bg-white tw-p-2;
      @apply lg:tw-w-[380px] lg:tw-gap-3 lg:tw-overflow-y-auto lg:tw-bg-transparent lg:tw-p-0;
      @apply xl:tw-w-[420px];
    }

    .view-right {
      @apply tw-flex tw-min-h-[400px] tw-flex-1 tw-flex-col tw-gap-3 tw-p-2;
      @apply lg:tw-min-h-0 lg:tw-overflow-hidden lg:tw-p-0;

      .view-items {
        // Stacked mobile layout: the page column scrolls, so give the list
        // room for ~4 item rows (72px row + 6px gap, ~53px card header).
        @apply tw-min-h-[380px] tw-flex-1;
        @apply lg:tw-min-h-0;
      }

      .money-pane {
        // Flat payment skin (posV2Host) has no serrated edges: uniform pad.
        @apply tw-flex-shrink-0 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-p-3;
        @apply lg:tw-max-h-[55%] lg:tw-overflow-y-auto;
      }

      // Compact (short desktop viewports): the items card and the money
      // strip fuse into one card; the money pane floats above the strip.
      &.is-compact {
        @apply tw-relative tw-gap-0;

        :deep(.pos-v2-view-items) {
          @apply tw-rounded-b-none tw-border-b-0;
        }

        .money-strip {
          @apply tw-rounded-b-xl tw-border tw-border-t-0 tw-border-neutral-200;
        }

        .money-pane {
          @apply tw-absolute tw-bottom-14 tw-left-2 tw-right-2 tw-z-30 tw-max-h-[75%] tw-overflow-y-auto tw-shadow-xl;
        }
      }
    }
  }
</style>
